<script lang="ts">
  /**
   * A round on one device, by taking turns (`docs/decisions/0059`): the
   * takeover that holds the round from the first hand-off to the results.
   *
   * - **The hand-off** before every turn: the shell's `HandOff` as a screen,
   *   concealing what is behind it, "Over to Noor", the standings so far, and
   *   "I am Noor", which the next player presses themselves (the shell arms
   *   it after half a second, so a stray tap as the device changes hands does
   *   nothing). Its quiet word, Escape and Back all end the round and go back
   *   to the setup (the names are kept; the round is not).
   * - **The turn**: the solo game screen (`GameScreen.svelte` with `turn`) on
   *   the round's seed, the player's name in the bar. Back asks "End Noor's
   *   turn?": ending it counts the turn as quit and the round goes on.
   * - **The results**, once everyone has played: the shell's
   *   `ResultsTemplate` with `standings`, ranked by the mode's own measure
   *   (`local/round.ts`), then Play again (the same players, a fresh seed)
   *   and Home.
   *
   * A finished round is kept in the device's own history, "On this device"
   * (`state/local-history.svelte.ts`), and **nowhere else**: nothing on this
   * screen reads or writes a personal best, the solo statistics, the daily or
   * a record. A round left before its end is not kept at all.
   */
  import { untrack } from 'svelte';

  import { HandOff, ResultsTemplate } from '@artibens/app-shell';
  import type { StandingsBlock } from '@artibens/app-shell';
  import type { ModeConfig } from '@interlock/modes';

  import { describeChoice } from '../nav/mode-choices.js';
  import { account } from '../state/account.svelte.js';
  import { recordLocalRound } from '../state/local-history.svelte.js';
  import {
    currentPlayer,
    figureFor,
    freshRoundSeed,
    headlineFor,
    isRoundOver,
    nextRound,
    playerAfterCurrent,
    rankedTurns,
    recordTurn,
    seedForTurn,
    standingsRows,
    startRound,
    toLocalSession,
    turnIndex,
  } from '../local/round.js';
  import type { LocalRound, TurnResult } from '../local/round.js';
  import GameScreen from './GameScreen.svelte';

  const {
    players,
    config,
    onSetup,
    onHome,
    onSettings,
    onHowToPlay,
    onplaying,
    paused = false,
  }: {
    /** The names, resolved, in turn order. */
    players: readonly string[];
    config: ModeConfig;
    /** End the round and go back to the setup, names kept. */
    onSetup: () => void;
    onHome: () => void;
    onSettings: () => void;
    onHowToPlay: () => void;
    /** Told whether a turn's board is on screen (the app gives a run the whole window on a phone on its side). */
    onplaying?: (playing: boolean) => void;
    /** True while a page of the app's is open over a turn. */
    paused?: boolean;
  } = $props();

  /** An unsigned 32-bit number from the platform's generator: a round's seed is not the clock. */
  function drawSeed(): number {
    return crypto.getRandomValues(new Uint32Array(1))[0] ?? 0;
  }

  /** A round's id in the history: random, 32 hex characters. */
  function newRoundId(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
  }

  let round: LocalRound = $state(
    untrack(() => startRound(players, config, freshRoundSeed(null, drawSeed))),
  );
  /** True from "I am Noor" until Noor's turn ends. */
  let playing = $state(false);

  const over = $derived(isRoundOver(round));
  const player = $derived(currentPlayer(round));
  const turn = $derived(turnIndex(round));
  const choice = $derived(describeChoice(round.config));

  $effect(() => {
    onplaying?.(playing);
  });

  function ready(): void {
    playing = true;
  }

  function endTurn(result: TurnResult): void {
    const next = recordTurn(round, result);
    round = next;
    playing = false;
    if (isRoundOver(next)) {
      recordLocalRound(
        toLocalSession(next, newRoundId(), Date.now()),
        account.status === 'signed-in',
      );
    }
  }

  function playAgain(): void {
    round = nextRound(round, freshRoundSeed(round.seed, drawSeed));
    playing = false;
  }

  /** The turn that just ended, said on the next hand-off. */
  const lastTurn = $derived(rankedTurns(round).at(-1) ?? null);

  const handOffDetail = $derived.by(() => {
    if (lastTurn === null) {
      return `${choice}. Everyone plays the same pieces, in the same order.`;
    }
    return `${lastTurn.name}: ${figureFor(round.config, lastTurn.result)}. The same pieces for ${player ?? ''}.`;
  });

  const soFar = $derived<StandingsBlock | undefined>(
    turn === 0
      ? undefined
      : {
          label: 'So far',
          rows: standingsRows(round),
          size: 'compact',
          testid: 'local-handoff-standings',
        },
  );

  const winners = $derived(rankedTurns(round).filter((each) => each.winner));
  const figure = $derived(
    winners.length === 1 && winners[0] !== undefined
      ? figureFor(round.config, winners[0].result)
      : undefined,
  );
  const savedNote = $derived(
    account.status === 'signed-in'
      ? 'Kept under On this device in the You tab, synced with your account. Never counted in your scores, stats or the records.'
      : 'Kept on this device only, under On this device in the You tab. Never counted in scores, stats or the records.',
  );
</script>

{#snippet note()}
  <p class="note" data-testid="local-results-note">{savedNote}</p>
{/snippet}

{#if over}
  <ResultsTemplate
    eyebrow={`On this device · ${choice}`}
    headline={headlineFor(round)}
    focusHeading
    {figure}
    figureIcon={figure === undefined ? undefined : 'trophy'}
    figureCaption={`Round ${String(round.number)} · ${String(round.players.length)} players, the same pieces for everyone`}
    notice={note}
    standings={{ label: 'Standings', rows: standingsRows(round), testid: 'local-standings' }}
    primary={{ label: 'Play again', onclick: playAgain, testid: 'local-again' }}
    secondary={{ label: 'Home', onclick: onHome, testid: 'local-home' }}
    testids={{
      root: 'local-results',
      headline: 'local-results-title',
      figure: 'local-results-figure',
    }}
  />
{:else if playing && player !== null}
  {#key `${String(round.number)}-${String(turn)}`}
    <GameScreen
      {config}
      turn={{
        player,
        seed: seedForTurn(round, turn),
        next: playerAfterCurrent(round),
        onEnd: endTurn,
      }}
      onFinish={() => undefined}
      onQuit={onSetup}
      {onSettings}
      {onHowToPlay}
      {paused}
    />
  {/key}
{:else if player !== null}
  <div class="behind" data-testid="local-behind"></div>
  <HandOff
    name={player}
    title={`Over to ${player}`}
    readyLabel={`I am ${player}`}
    eyebrow={round.number > 1
      ? `Round ${String(round.number)} · turn ${String(turn + 1)} of ${String(round.players.length)}`
      : `Turn ${String(turn + 1)} of ${String(round.players.length)}`}
    detail={handOffDetail}
    standings={soFar}
    onready={ready}
    back={{ label: 'End the round', onclick: onSetup, testid: 'local-handoff-back' }}
    testid="local-handoff"
    testids={{ title: 'local-handoff-title', ready: 'local-handoff-ready' }}
  />
{/if}

<style>
  /* What is behind the hand-off: the ground, nothing of the last board. */
  .behind {
    flex: 1;
    background: var(--ui-ground);
  }

  /* On the deep gradient, as the solo results' own lines. */
  .note {
    margin: 0;
    text-align: center;
    font: var(--ui-text-label);
    line-height: 1.45;
    color: var(--ui-on-deep);
  }
</style>
